import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import DropdownSearch from 'shared/components/DropdownSearch';
import { useTranslation } from 'react-i18next';
import { Switch } from 'shared/components/form/Inputs';
import { colors } from 'shared/defaultTheme';
import { Checkbox } from 'shared/components/form/CheckBox';
import { ReactComponent as SearchIcon } from 'assets/icon-search.svg';
import { ReactComponent as InfoIcon } from 'assets/icon-information.svg';
import ConfirmModal from 'shared/components/ConfirmModal';
import { ClientsContactSelectorContainer, SearchContacts } from './styles';

const ClientsContactSelector = ({ contacts, setSelectedContacts, selectedContacts }) => {
  const { t } = useTranslation();
  const [groupedContacts, setGroupedContacts] = useState({});
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [isAllContactsSelected, setIsAllContactsSelected] = useState(false);
  const [clientContactsFilter, setClientContactsFilter] = useState({});
  const [warningModal, setWarningModal] = useState(false);

  useEffect(() => {
    if (selectedContacts.length > 50) {
      setWarningModal(true);
    }
  }, [selectedContacts]);

  useEffect(() => {
    const grouped = {};
    const clientsArray = [];
    const clientContactsFilterObj = {};
    contacts.forEach((contact) => {
      const { clientId, clientName } = contact;
      if (!grouped[clientId]) {
        clientsArray.push({ id: clientId, name: clientName });
        grouped[clientId] = [];
        clientContactsFilterObj[clientId] = '';
      }
      grouped[clientId].push({ ...contact, ...{ isSelected: false } });
    });
    setGroupedContacts(grouped);
    setClients(clientsArray);
    setClientContactsFilter(clientContactsFilterObj);
  }, [contacts]);

  useEffect(() => {
    const isAllSelected = Object.keys(groupedContacts).every((key) =>
      groupedContacts[key].every((contact) => contact.isSelected)
    );
    setIsAllContactsSelected(isAllSelected);
    setSelectedContacts(groupedContacts);
  }, [groupedContacts]);

  const handleCheckContact = (contact) => {
    const groupedAux = { ...groupedContacts };
    const grouped = groupedContacts[contact.clientId].map((currentContact) => {
      let currentContactAux = { ...currentContact };
      if (currentContact.mail === contact.mail) {
        currentContactAux = {
          ...currentContact,
          ...{ isSelected: !currentContact.isSelected },
        };
      }
      return currentContactAux;
    });
    groupedAux[contact.clientId] = grouped;
    setGroupedContacts(groupedAux);
  };

  const onSearchContacts = (e, clientId) => {
    e.preventDefault();
    setClientContactsFilter({ ...clientContactsFilter, ...{ [clientId]: e.target.value } });
  };

  const checkSelectedContacts = (clientId) => {
    let isAllSelected = true;
    groupedContacts[clientId].forEach((contact) => {
      if (!contact.isSelected) {
        isAllSelected = false;
      }
    });
    return isAllSelected;
  };

  const handleSelectAllClientContacts = (clientId) => {
    const value = !checkSelectedContacts(clientId);
    const groupedAux = { ...groupedContacts };
    const grouped = groupedContacts[clientId].map((currentContact) => {
      let currentContactAux = { ...currentContact };
      currentContactAux = {
        ...currentContact,
        ...{ isSelected: value },
      };
      return currentContactAux;
    });
    groupedAux[clientId] = grouped;
    setGroupedContacts(groupedAux);
  };

  const handleSelectAllContacts = () => {
    const groupedAux = { ...groupedContacts };
    Object.entries(groupedContacts).forEach((clientContacts) => {
      const grouped = groupedContacts[clientContacts[0]].map((currentContact) => {
        let currentContactAux = { ...currentContact };
        currentContactAux = {
          ...currentContact,
          ...{ isSelected: !isAllContactsSelected },
        };
        return currentContactAux;
      });
      groupedAux[clientContacts[0]] = grouped;
    });
    setGroupedContacts(groupedAux);
  };

  const onCloseWarningModal = () => {
    setWarningModal(false);
  };

  return (
    <ClientsContactSelectorContainer>
      <div className="w-100">
        <div className="selected-contacts-info">
          <InfoIcon fill={colors.lightBlue} width="20px" height="20px" />
          <span>{t('client_access_manager_selected_contacts_info')}</span>
          <span
            className="selected-contacts-quantity"
            style={{
              backgroundColor: selectedContacts.length > 50 ? colors.venetianRed : colors.zambezi,
            }}
          >
            {selectedContacts?.length}
          </span>
        </div>
        <DropdownSearch
          id="access-manager-client-dropdown-search"
          options={clients.map((currentClient) => ({
            value: currentClient.id,
            name: currentClient.name,
          }))}
          value={selectedClient}
          onChange={(e) => setSelectedClient(e)}
          placeholder={t('home_search_client')}
          className="client-dropdown-search w-100"
        />
      </div>
      {!selectedClient && (
        <div className="my-3 d-flex align-items-center justify-content-start w-100">
          <span className="select-all-client">{t('client_access_manager_select_all_clients')}</span>
          <Switch
            className="text-uppercase"
            id="switchActiveAll"
            color={isAllContactsSelected ? colors.bilbao : colors.error}
            value={!isAllContactsSelected}
            onChange={() => handleSelectAllContacts()}
            classNameMain="ml-1"
          />
        </div>
      )}
      <div className="w-100 my-3">
        <div className="form-container">
          {clients.map((currentClient) => (
            <div key={currentClient.id}>
              {((!!selectedClient && selectedClient === currentClient.id) || !selectedClient) && (
                <div className="w-100" key={currentClient.id}>
                  <div className="w-100 client-name-box mb-3">
                    <strong>{currentClient.name}</strong>
                  </div>
                  <div className="mb-2 d-flex align-items-center">
                    <span className="select-all-client">
                      {t('client_access_manager_select_all_clients')}
                    </span>
                    <Switch
                      className="text-uppercase"
                      id={`switchActiveAll-${currentClient.id}`}
                      color={checkSelectedContacts(currentClient.id) ? colors.bilbao : colors.error}
                      value={!checkSelectedContacts(currentClient.id)}
                      onChange={() => handleSelectAllClientContacts(currentClient.id)}
                      classNameMain="ml-1"
                    />
                  </div>
                  {groupedContacts[currentClient.id] && (
                    <div className="contact-box-container mb-4">
                      <div className="mb-3 search-contacts-container">
                        <div className="form search-contacts-form">
                          <SearchContacts
                            Icon={() => null}
                            borderRadius={2}
                            className="mb-4 w-25 m-0 h-50"
                            inputClassName="w-100"
                            buttonClassName=""
                            withButtonClear
                            rightIcon={<SearchIcon fill={colors.black} />}
                            placeholder={t('client_search_contact')}
                            value={clientContactsFilter[currentClient.id]}
                            onChange={(e) => onSearchContacts(e, currentClient.id)}
                          />
                        </div>
                      </div>
                      <ul className="share-contacts-container">
                        {groupedContacts[currentClient.id]?.map((contact) => (
                          <React.Fragment key={contact.mail}>
                            {(`${contact.name} ${contact.surname}`
                              .toLowerCase()
                              .includes(clientContactsFilter[currentClient.id]?.toLowerCase()) ||
                              !clientContactsFilter[currentClient.id]) && (
                              <li
                                className="mb-3 list-contact share-list-contact d-flex"
                                key={contact.mail}
                              >
                                <Checkbox
                                  type="checkbox"
                                  id="filterCheckAll"
                                  label=""
                                  checked={contact.isSelected}
                                  onClick={() => handleCheckContact(contact)}
                                />
                                <span className="mt-n1">{`${contact.name} ${contact.surname}`}</span>
                              </li>
                            )}
                          </React.Fragment>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <ConfirmModal
        isOpen={warningModal}
        toggle={onCloseWarningModal}
        confirmMessage={t('client_access_manager_limit_message')}
        okText={t('general_button_ok')}
        onOk={onCloseWarningModal}
      />
    </ClientsContactSelectorContainer>
  );
};

ClientsContactSelector.propTypes = {
  contacts: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object])).isRequired,
  setSelectedContacts: PropTypes.func.isRequired,
  selectedContacts: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object])).isRequired,
};

export default ClientsContactSelector;
